import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { SelectDropdown } from '../../components/SelectDropdown';
import { ROUTES } from '../../consts/general';
import { DAILY_CHANNEL_METRICS } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;
const DEFAULT_END_DATE = format(new Date(), 'yyyy-MM-dd');
const DEFAULT_START_DATE = format(subDays(new Date(), 30), 'yyyy-MM-dd');

const CHANNEL_OPTIONS = [
  { label: 'All Channels', value: '' },
  { label: 'Shopify', value: 'Shopify' },
  { label: 'Amazon', value: 'Amazon' },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('en-US') : '—';
};

const DailyChannelMetricsReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);
  const [channel, setChannel] = useState('');

  const columns = useMemo(
    () => [
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'channel', label: 'Channel', type: 'string' },
      { key: 'orders', label: 'Orders', type: 'number' },
      { key: 'gross_sales', label: 'Gross Sales', type: 'currency' },
      { key: 'total_paid', label: 'Total Paid', type: 'currency' },
      { key: 'taxes', label: 'Taxes', type: 'currency' },
      { key: 'refunds', label: 'Refunds', type: 'currency' },
      { key: 'ad_spend', label: 'Ad Spend', type: 'currency' },
      { key: 'ppc_sales', label: 'PPC Sales', type: 'currency' },
      { key: 'tw_orders', label: 'TW Orders', type: 'number' },
      { key: 'tw_order_revenue', label: 'TW Order Revenue', type: 'currency' },
      { key: 'tw_gross_product_sales', label: 'TW Gross Sales', type: 'currency' },
      { key: 'revenue_variance_pct', label: 'Variance %', type: 'number' },
    ],
    []
  );

  const buildParams = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams({
        startDate,
        endDate,
        ...extra,
      });
      if (channel) params.append('channel', channel);
      return params;
    },
    [startDate, endDate, channel]
  );

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = buildParams({ page: String(currentPage), pageSize: String(PAGE_SIZE) });
      const res = await fetch(`/api/v1/reports/${DAILY_CHANNEL_METRICS}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotalPages(json.totalPages || 1);
      setTotalRecords(json.totalRecords || 0);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [startDate, endDate, currentPage, buildParams, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const renderVariance = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    const highlight = num > 5;
    return (
      <span className={highlight ? 'text-red-600 font-medium' : 'text-gray-900'}>
        {num.toFixed(2)}%
      </span>
    );
  };

  const renderRows = () =>
    (data || []).map((row, idx) => (
      <tr key={`${row.date || 'date'}-${row.channel || 'channel'}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.channel || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(row.orders)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.gross_sales)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.total_paid)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.taxes)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.refunds)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.ad_spend)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.ppc_sales)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatNumber(row.tw_orders)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.tw_order_revenue)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.tw_gross_product_sales)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{renderVariance(row.revenue_variance_pct)}</td>
      </tr>
    ));

  const filtersComponent = (
    <div className="contents">
      <SelectDropdown
        selectClassname="!bg-white"
        options={CHANNEL_OPTIONS}
        value={channel}
        onChange={(value) => {
          setChannel(value);
          setCurrentPage(1);
        }}
      />
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: newStartDate, endDate: newEndDate }) => {
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          setCurrentPage(1);
        }}
        label="Date Range"
      />
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={columns}
      filename={DAILY_CHANNEL_METRICS}
      onExport={async () => {
        const params = buildParams();
        const res = await fetch(`/api/v1/reports/${DAILY_CHANNEL_METRICS}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${DAILY_CHANNEL_METRICS}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
          {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <ReportTemplate
      reportId={DAILY_CHANNEL_METRICS}
      description="Daily numbers per sales channel (America/Denver days): orders, gross sales, taxes, refunds, ad spend, and PPC sales, with Triple Whale shown alongside as an accuracy cross-check"
      breadcrumbs={breadcrumbs}
      onBack={handleBackToReports}
      marginFromSidebar={marginFromSidebar}
      isLoading={isLoading}
      error={error}
      filters={filtersComponent}
      exportButton={exportButton}
      data={data}
      columns={columns}
      pagination={paginationComponent}
      emptyMessage="No daily channel metrics found for the selected filters. Data is populated by the Daily Channel Metrics cron."
    >
      {renderRows()}
    </ReportTemplate>
  );
};

export default DailyChannelMetricsReport;
